/**
 * Country Preferences Utility
 * Manages user's preferred country for frequent visa checks
 */

// Storage key for country preferences
const COUNTRY_PREFERENCE_KEY = 'visa_checker_country_preference';

/**
 * Get the user's preferred country from localStorage
 * @returns {Object|null} Preferred country object or null if not set
 */
export const getPreferredCountry = () => {
  try {
    const saved = localStorage.getItem(COUNTRY_PREFERENCE_KEY);
    return saved ? JSON.parse(saved) : null;
  } catch (error) {
    console.warn('Error loading country preference:', error);
    return null;
  }
};

/**
 * Save the user's preferred country to localStorage
 * @param {Object} country - Country object to save as preference
 */
export const setPreferredCountry = (country) => {
  try {
    if (country) {
      const preferenceData = {
        country,
        timestamp: Date.now(),
        version: '1.0'
      };
      localStorage.setItem(COUNTRY_PREFERENCE_KEY, JSON.stringify(preferenceData));
      console.log('Country preference saved:', country.name);
    }
  } catch (error) {
    console.error('Error saving country preference:', error);
  }
};

/**
 * Clear the user's country preference
 */
export const clearPreferredCountry = () => {
  try {
    localStorage.removeItem(COUNTRY_PREFERENCE_KEY);
    console.log('Country preference cleared');
  } catch (error) {
    console.error('Error clearing country preference:', error);
  }
};

/**
 * Check if a country is the user's preferred country
 * @param {Object} country - Country object to check
 * @returns {boolean} True if this is the preferred country
 */
export const isPreferredCountry = (country) => {
  const preferred = getPreferredCountry();
  return preferred && preferred.country && preferred.country.code === country.code;
};

/**
 * Sort countries array to put preferred country first
 * @param {Array} countries - Array of country objects
 * @returns {Array} Sorted array with preferred country first
 */
export const sortCountriesWithPreference = (countries) => {
  const preferred = getPreferredCountry();
  
  if (!preferred || !preferred.country) {
    return countries; // No preference, return as-is
  }
  
  const preferredCountry = preferred.country;
  const otherCountries = countries.filter(country => country.code !== preferredCountry.code);
  const preferredCountryInList = countries.find(country => country.code === preferredCountry.code);
  
  if (preferredCountryInList) {
    return [preferredCountryInList, ...otherCountries];
  }
  
  return countries; // Preferred country not in list, return as-is
};

/**
 * Get preference info for display purposes
 * @returns {Object} Preference information
 */
export const getPreferenceInfo = () => {
  const preferred = getPreferredCountry();
  
  if (!preferred) {
    return {
      hasPreference: false,
      country: null,
      savedAt: null,
      isRecent: false
    };
  }
  
  const savedAt = new Date(preferred.timestamp);
  const daysSinceSet = Math.floor((Date.now() - preferred.timestamp) / (1000 * 60 * 60 * 24));
  
  return {
    hasPreference: true,
    country: preferred.country,
    savedAt,
    daysSinceSet,
    isRecent: daysSinceSet <= 30 // Consider recent if set within 30 days
  };
};
